import { DetailView } from '@components/enitity'
import { Loader } from '@components/loader/Loader'
import { FlatsWidget } from '@features/flats/components/FlatsWidget'
import { LandlordFlatContracts } from '@features/flats/components/LandlordFlatContracts'
import { LandlordFlatSettlements } from '@features/flats/components/LandlordFlatSettlements'
import { LandlordPaymentsTable } from '@features/payments/components/LandlordPaymentsTable'
import { useFlatsDetail } from '@services/flats/queries/useFlatsDetail'
import { FileText, Info, type LucideIcon, Receipt, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { RentModel } from 'types/api'

import { cn } from '@/lib/utils'

export function LandlordFlatsDetail() {
  const { id } = useParams()
  const { t } = useTranslation('flat')
  const [searchParams, setSearchParams] = useSearchParams()
  const { data, isLoading } = useFlatsDetail({ id })

  if (isLoading) {
    return <Loader />
  }

  if (!data) {
    return <div>{t('not_found', { defaultValue: 'Flat not found' })}</div>
  }

  const tabs: { key: string; label: string; icon: LucideIcon }[] = [
    { key: 'detail', label: t('navigation.detail'), icon: Info },
    { key: 'contract', label: t('navigation.contract'), icon: FileText },
    { key: 'payments', label: t('navigation.payments'), icon: Wallet },
    { key: 'settlements', label: t('navigation.settlements'), icon: Receipt }
  ]

  const active = searchParams.get('tab') || 'detail'
  const setActive = (key: string) =>
    setSearchParams(
      (prev) => {
        prev.set('tab', key)
        return prev
      },
      { replace: true }
    )

  const panels: Record<string, React.ReactNode> = {
    detail: <FlatsWidget flat={data} />,
    contract: <LandlordFlatContracts flat={data} />,
    payments: (
      <LandlordPaymentsTable
        rents={data.flat_payments as unknown as RentModel[]}
      />
    ),
    settlements: <LandlordFlatSettlements flat={data} />
  }

  return (
    <div className="space-y-6">
      <DetailView.Breadcrumb
        className="mb-0 pb-4"
        parentLabel={t('title')}
        parentTo="/landlord/flats"
        current={data.title}
      />

      <div className="-mx-1 overflow-x-auto px-1">
        <div className="inline-flex items-center gap-1 rounded-full border border-ink/[0.06] bg-ink/[0.03] p-1">
          {tabs.map(({ key, label, icon: Icon }) => {
            const isTabActive = active === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActive(key)}
                className={cn(
                  'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border-0 px-4 py-2 text-sm font-semibold transition-colors',
                  isTabActive
                    ? 'bg-white text-ink shadow-sm'
                    : 'bg-transparent text-muted-foreground hover:text-ink'
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            )
          })}
        </div>
      </div>

      <div>{panels[active] ?? panels.detail}</div>
    </div>
  )
}
